import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Play,
  Settings,
  BarChart3,
  FileText,
  Pin,
  PinOff,
  CalendarClock
} from 'lucide-react';
import UserProfileMenu from './common/UserProfileMenu';
import AppLogo from './common/AppLogo';
import { MobileMenu } from './common/MobileMenu';
import ThemeToggle from './common/ThemeToggle';
import { MobileMenuButton } from './common/MobileMenuButton';
import { PWAInstallButton } from './common/PWAInstallButton';
import { useMobileMenu } from '../hooks/useMobileMenu';

import { logger } from '../utils/logger';
interface SidebarProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
}

const navigation = [
  { name: 'QA Dashboard', href: '/dashboard', icon: Play, available: true, badge: null },
  { name: 'Test Automation', href: '/flows', icon: FileText, available: true, badge: null },
  { name: 'Scheduled Tests', href: '/scheduled-tests', icon: CalendarClock, available: true, badge: null },
  { name: 'Test Reports', href: '/reports', icon: BarChart3, available: true, badge: null },
  // { name: 'AI Insights', href: '/ai', icon: Bot, available: false, badge: null }, // Hidden for now
  { name: 'Configuration', href: '/configuration', icon: Settings, available: true, badge: null },
];

const Sidebar: React.FC<SidebarProps> = ({ isOpen, setIsOpen }) => {
  const location = useLocation();
  
  // Mobile menu state (using new hook)
  const { isOpen: isMobileMenuOpen, open: openMobileMenu, close: closeMobileMenu } = useMobileMenu();
  
  // Hover-expand state management (desktop only)
  const [isHovered, setIsHovered] = useState(false);
  const [isPinned, setIsPinned] = useState(() => {
    // Check localStorage for saved pin state
    const saved = localStorage.getItem('sidebarPinned');
    return saved === 'true';
  });
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  // Load app settings
  const [appSettings, setAppSettings] = useState({ appName: 'QA Agent', appLogo: '', appInitials: 'QA' });
  
  useEffect(() => {
    const saved = localStorage.getItem('appSettings');
    if (saved) {
      try {
        setAppSettings(JSON.parse(saved));
      } catch (e) {
        logger.error('Failed to load app settings:', { error: e instanceof Error ? e.message : String(e) });
      }
    }
  }, []);

  // Calculate expanded state (must be defined before useEffect that uses it)
  const isExpanded = isPinned || isHovered;

  // Mouse leave handler - only collapse if mouse truly left sidebar
  const handleMouseLeave = (e: React.MouseEvent) => {
    if (!isPinned && !userMenuOpen) {
      setIsHovered(false);
    }
  };

  // Reset user menu state when sidebar collapses
  useEffect(() => {
    if (!isExpanded) {
      setUserMenuOpen(false);
    }
  }, [isExpanded]);

  // Shared sidebar content (used in both mobile and desktop)
  const sidebarContent = (isMobile: boolean = false) => (
    <>
      {/* Header - Clickable to Dashboard */}
      <div className="flex items-center justify-between h-16 px-3 border-b border-gray-200 dark:border-slate-700 bg-white/80 dark:bg-slate-800/80 backdrop-blur-sm">
        <Link 
          to="/dashboard" 
          className="flex-1 flex items-center justify-center hover:opacity-80 transition-opacity cursor-pointer"
          onClick={() => isMobile && closeMobileMenu()}
        >
          {(isMobile || isExpanded) ? (
            <AppLogo
              logo={appSettings.appLogo}
              name={appSettings.appName}
              initials={appSettings.appInitials}
              size="sm"
              showName={true}
            />
          ) : (
            <div className="flex flex-col items-center w-full py-1">
              <AppLogo
                logo={appSettings.appLogo}
                name={appSettings.appName}
                initials={appSettings.appInitials}
                size="sm"
                showName={false}
              />
              <span className="mt-1.5 text-[9px] font-bold text-gray-500 dark:text-slate-400 tracking-wider">
                {appSettings.appInitials}
              </span>
            </div>
          )}
        </Link>

        {/* Action buttons - only show when expanded (desktop) or on mobile */}
        {(isMobile || isExpanded) && (
          <div 
            className="flex items-center gap-1 flex-shrink-0"
            style={{ animation: isMobile ? 'none' : 'fadeInText 200ms ease-in 150ms both' }}
          >
            {/* PWA Install Button */}
            <PWAInstallButton variant="header" />
            
            {/* Theme toggle */}
            <ThemeToggle />
          </div>
        )}

        {/* Pin button - only show when expanded on desktop */}
        {!isMobile && isExpanded && (
          <button
            onClick={() => {
              const newPinState = !isPinned;
              setIsPinned(newPinState);
              localStorage.setItem('sidebarPinned', String(newPinState));
            }}
            className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors flex-shrink-0"
            title={isPinned ? "Unpin sidebar" : "Pin sidebar open"}
            style={{ animation: 'fadeInText 200ms ease-in 150ms both' }}
          >
            {isPinned ? (
              <Pin className="h-4 w-4 text-blue-600 dark:text-blue-400" />
            ) : (
              <PinOff className="h-4 w-4 text-gray-400 dark:text-slate-400" />
            )}
          </button>
        )}
      </div>

      {/* ✅ WCAG 2.1: Semantic Navigation Landmark with ARIA */}
      {/* Industry Standard: Primary navigation region */}
      <nav 
        className="flex-1 px-2 py-4 space-y-1" 
        role="navigation" 
        aria-label="Main navigation"
      >
        {navigation.map((item) => {
          const Icon = item.icon;
          const isActive = location.pathname === item.href;
          const isComingSoon = !item.available;

          if (isComingSoon) {
            return (
              <div
                key={item.name}
                className="group flex items-center px-2 py-2 text-sm font-medium rounded-md cursor-not-allowed bg-gray-50 opacity-60"
              >
                <Icon className="h-5 w-5 flex-shrink-0 text-gray-300" />
                {(isMobile || isExpanded) && (
                  <div className="ml-3 flex items-center justify-between w-full">
                    <span className="text-gray-400">{item.name}</span>
                    <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded-full font-medium">
                      Coming Soon
                    </span>
                  </div>
                )}
                {!isMobile && !isExpanded && (
                  <div className="absolute left-16 ml-2 px-2 py-1 bg-gray-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity z-50 whitespace-nowrap">
                    {item.name} - Coming Soon
                  </div>
                )}
              </div>
            );
          }

          return (
            <Link
              key={item.name}
              to={item.href}
              className={`group relative flex items-center px-3 py-3 text-sm font-semibold rounded-xl transition-all duration-200 ${
                isActive
                  ? 'bg-gradient-to-r from-blue-50 to-blue-100 dark:from-blue-900/30 dark:to-blue-800/30 text-blue-700 dark:text-blue-300 shadow-sm border-l-4 border-blue-600 dark:border-blue-400 ml-0'
                  : 'text-gray-600 dark:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-800 hover:text-gray-900 dark:hover:text-slate-100 ml-0 hover:ml-1 hover:shadow-sm'
              }`}
              onClick={() => {
                if (isMobile) {
                  closeMobileMenu();
                } else if (window.innerWidth < 1024) {
                  setIsOpen(false);
                }
              }}
            >
              <Icon className={`h-5 w-5 flex-shrink-0 transition-all duration-200 ${
                isActive ? 'text-blue-600 dark:text-blue-400' : 'text-gray-400 dark:text-slate-400 group-hover:text-gray-600 dark:group-hover:text-slate-200 group-hover:scale-110'
              }`} />
              {(isMobile || isExpanded) && (
                <>
                  <span 
                    className="ml-3 whitespace-nowrap overflow-hidden"
                    style={{ animation: isMobile ? 'none' : 'fadeInText 200ms ease-in 150ms both' }}
                  >
                    {item.name}
                  </span>
                  {item.badge && (
                    <span 
                      className="ml-auto px-2 py-0.5 bg-rose-100 dark:bg-rose-900/50 text-rose-700 dark:text-rose-300 text-xs font-bold rounded-full"
                      style={{ animation: isMobile ? 'none' : 'fadeInText 200ms ease-in 200ms both' }}
                    >
                      {item.badge}
                    </span>
                  )}
                </>
              )}
              {/* Badge dot when collapsed on desktop */}
              {!isMobile && !isExpanded && item.badge && (
                <span className="absolute top-2 right-2 w-2 h-2 bg-rose-500 dark:bg-rose-400 rounded-full"></span>
              )}
              {/* Tooltip when collapsed on desktop */}
              {!isMobile && !isExpanded && (
                <div className="absolute left-full ml-2 px-3 py-2 bg-gray-900 dark:bg-slate-700 text-white dark:text-slate-100 text-sm rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 whitespace-nowrap z-50 shadow-xl">
                  {item.name}
                </div>
              )}
            </Link>
          );
        })}
      </nav>

      {/* Footer - User Profile */}
      <div className="p-3 border-t border-gray-200 dark:border-slate-700 bg-white/80 dark:bg-slate-800/80 backdrop-blur-sm">
        <UserProfileMenu 
          isExpanded={isMobile || isExpanded} 
          onMenuOpen={() => setUserMenuOpen(true)} 
          onMenuClose={() => setUserMenuOpen(false)}
        />
      </div>
    </>
  );

  return (
    <>
      {/* Mobile Menu Button - Fixed position, visible only on mobile */}
      <div className="lg:hidden fixed top-4 left-4 z-[250]">
        <MobileMenuButton
          isOpen={isMobileMenuOpen}
          onClick={openMobileMenu}
          ariaLabel="Open navigation menu"
        />
      </div>

      {/* Mobile Menu - Drawer navigation */}
      <MobileMenu
        isOpen={isMobileMenuOpen}
        onClose={closeMobileMenu}
        position="left"
      >
        <div className="flex flex-col h-full">
          {sidebarContent(true)}
        </div>
      </MobileMenu>

      {/* Desktop Sidebar - Hidden on mobile, visible on desktop */}
      <div 
        className={`hidden lg:flex fixed inset-y-0 left-0 ${isHovered ? 'z-[200]' : 'z-[100]'} flex-col bg-gradient-to-b from-white via-gray-50 to-white dark:from-slate-900 dark:via-slate-900 dark:to-slate-900 border-r border-gray-300 dark:border-slate-700 shadow-lg transition-all duration-300 ease-in-out ${
          isExpanded ? 'w-64' : 'w-16'
        }`}
        onMouseEnter={(e) => {
          // Only expand if mouse is within the collapsed sidebar width (16 = 64px)
          if (!isPinned && e.clientX <= 64) {
            setIsHovered(true);
          }
        }}
        onMouseMove={(e) => {
          // Continue checking mouse position while moving
          if (!isPinned && !isHovered && e.clientX <= 64) {
            setIsHovered(true);
          }
        }}
        onMouseLeave={handleMouseLeave}
      >
        {/* Shadow overlay when expanded and not pinned */}
        {isExpanded && !isPinned && (
          <div className="fixed inset-0 bg-black/5 -z-10 pointer-events-none" />
        )}

        {/* Use shared sidebar content for desktop */}
        {sidebarContent(false)}
      </div>
    </>
  );
};

export default Sidebar;
